import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  TextField,
  Button,
  Alert,
  Snackbar,
  InputAdornment,
  Divider,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
  CheckCircle as CheckCircleOutlineIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import config from '../config';
import GoogleMapsSettings from '../components/settings/GoogleMapsSettings';
import BulkImportDialog from '../components/organizations/BulkImportDialog';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Settings() {
  const { getToken, isAdmin, token } = useAuth();
  const { socket } = useSocket();
  const [activeTab, setActiveTab] = useState(0);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // Xero state
  const [xeroStatus, setXeroStatus] = useState(null);
  const [xeroLoading, setXeroLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Xero activity log
  const [xeroActivity, setXeroActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  
  // Default price settings
  const [defaultPrices, setDefaultPrices] = useState({
    adSupportedSchoolPrice: 480.00,
    paidSchoolPrice: 1200.00
  });
  
  // Stationery settings
  const [stationerySettings, setStationerySettings] = useState({
    marginPercentage: 25,
    artworkFee: 45,
    setupFee: 25,
    gstPercentage: 10
  });

  // Payroll / commission settings
  const [payrollSettings, setPayrollSettings] = useState({
    commission_target_bonus: 500.00,
  });
  const [originalPayrollSettings, setOriginalPayrollSettings] = useState({});
  
  const [originalPrices, setOriginalPrices] = useState({});
  const [originalStationerySettings, setOriginalStationerySettings] = useState({});

  // Auto-switch to Xero tab and show result when redirected back from Xero OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xeroParam = params.get('xero');
    if (xeroParam === 'connected') {
      setActiveTab(3);
      setSnackbar({ open: true, message: 'Xero connected successfully!', severity: 'success' });
      window.history.replaceState({}, '', window.location.pathname);
    } else if (xeroParam === 'error') {
      setActiveTab(3);
      const msg = params.get('message') || 'Xero connection failed.';
      const isCancelled = msg.toLowerCase().includes('cancel');
      setSnackbar({ open: true, message: msg, severity: isCancelled ? 'warning' : 'error' });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchXeroStatus = useCallback(async () => {
    try {
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/xero/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setXeroStatus(data);
      }
    } catch (e) {
      console.warn('Could not fetch Xero status', e);
    }
  }, [getToken]);

  const fetchXeroActivity = useCallback(async () => {
    try {
      setActivityLoading(true);
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/xero/activity`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setXeroActivity(data.events || []);
      }
    } catch (e) {
      console.warn('Could not fetch Xero activity', e);
    } finally {
      setActivityLoading(false);
    }
  }, [getToken]);

  // Live Socket.IO updates — prepend new events and show a toast for failures
  useEffect(() => {
    if (!socket) return;
    const handler = (event) => {
      setXeroActivity(prev => [event, ...prev].slice(0, 100));
      if (!event.success) {
        setSnackbar({ open: true, message: `Xero: ${event.message}`, severity: 'error' });
      }
    };
    socket.on('xero:activity', handler);
    return () => socket.off('xero:activity', handler);
  }, [socket]);

  useEffect(() => {
    fetchSettings();
    fetchXeroStatus();
    fetchXeroActivity();
  }, [fetchXeroStatus, fetchXeroActivity]);

  // After Xero OAuth redirect, parse ?xero=connected|error and auto-switch to Xero tab
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xeroParam = params.get('xero');
    if (!xeroParam) return;

    // Switch to Xero tab (index 3)
    setActiveTab(3);

    if (xeroParam === 'connected') {
      setSnackbar({ open: true, message: 'Xero connected successfully!', severity: 'success' });
      fetchXeroStatus();
    } else if (xeroParam === 'cancelled') {
      setSnackbar({ open: true, message: 'Xero connection cancelled', severity: 'warning' });
    } else if (xeroParam === 'error') {
      const msg = params.get('message') || 'Xero connection failed';
      setSnackbar({ open: true, message: `Xero error: ${msg}`, severity: 'error' });
    }

    // Clean the URL so a page refresh doesn't re-trigger
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const response = await fetch(`${config.apiUrl}/api/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.data) {
          const settings = data.data;
          const prices = {
            adSupportedSchoolPrice: parseFloat(settings.adSupportedSchoolPrice) || 480.00,
            paidSchoolPrice: parseFloat(settings.paidSchoolPrice) || 1200.00
          };
          const stationery = {
            marginPercentage: parseFloat(settings.stationeryMarginPercentage) || 25,
            artworkFee: parseFloat(settings.stationeryArtworkFee) || 45,
            setupFee: parseFloat(settings.stationerySetupFee) || 25,
            gstPercentage: parseFloat(settings.stationeryGstPercentage) || 10
          };
          const payroll = {
            commission_target_bonus: parseFloat(settings.commission_target_bonus) || 500.00,
          };
          setDefaultPrices(prices);
          setOriginalPrices({ ...prices });
          setStationerySettings(stationery);
          setOriginalStationerySettings({ ...stationery });
          setPayrollSettings(payroll);
          setOriginalPayrollSettings({ ...payroll });
        }
      } else {
        console.warn('Settings API not available, using default values');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Continue with default values if API is not available
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setLoading(true);
      const token = await getToken();
      
      const response = await fetch(`${config.apiUrl}/api/settings`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adSupportedSchoolPrice: defaultPrices.adSupportedSchoolPrice,
          paidSchoolPrice: defaultPrices.paidSchoolPrice,
          stationeryMarginPercentage: stationerySettings.marginPercentage,
          stationeryArtworkFee: stationerySettings.artworkFee,
          stationerySetupFee: stationerySettings.setupFee,
          stationeryGstPercentage: stationerySettings.gstPercentage,
          commission_target_bonus: payrollSettings.commission_target_bonus,
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        setOriginalPrices({ ...defaultPrices });
        setOriginalStationerySettings({ ...stationerySettings });
        setOriginalPayrollSettings({ ...payrollSettings });
        setSnackbar({
          open: true,
          message: 'Settings saved successfully',
          severity: 'success'
        });
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: 'Error saving settings. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (field, value) => {
    setDefaultPrices(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleStationeryChange = (field, value) => {
    setStationerySettings(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleXeroConnect = async () => {
    try {
      setXeroLoading(true);
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/xero/auth`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to start Xero connection', severity: 'error' });
    } finally {
      setXeroLoading(false);
    }
  };

  const handleXeroDisconnect = async () => {
    if (!window.confirm('Disconnect from Xero? This will stop all invoice syncing until reconnected.')) return;
    try {
      setXeroLoading(true);
      const token = await getToken();
      await fetch(`${config.apiUrl}/api/xero/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setXeroStatus({ connected: false });
      setSnackbar({ open: true, message: 'Xero disconnected successfully', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to disconnect Xero', severity: 'error' });
    } finally {
      setXeroLoading(false);
    }
  };

  const [xeroVerifyResult, setXeroVerifyResult] = useState(null);
  const [xeroVerifying, setXeroVerifying] = useState(false);
  const handleXeroVerify = async () => {
    try {
      setXeroVerifying(true);
      setXeroVerifyResult(null);
      const token = await getToken();
      const resp = await fetch(`${config.apiUrl}/api/xero/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      setXeroVerifyResult(data);
    } catch (e) {
      setXeroVerifyResult({ success: false, message: `Network error: ${e.message}` });
    } finally {
      setXeroVerifying(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const hasChanges = () => {
    return JSON.stringify(defaultPrices) !== JSON.stringify(originalPrices);
  };

  const resetChanges = () => {
    setDefaultPrices({ ...originalPrices });
  };

  const hasStationeryChanges = () => {
    return JSON.stringify(stationerySettings) !== JSON.stringify(originalStationerySettings);
  };

  const resetStationeryChanges = () => {
    setStationerySettings({ ...originalStationerySettings });
  };

  return (
    <Container maxWidth={false} sx={{ mt: 2, mb: 2 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <SettingsIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            Settings
          </Typography>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
            <Tab label="School Orders" id="settings-tab-0" aria-controls="settings-tabpanel-0" />
            <Tab label="Stationery Orders" id="settings-tab-1" aria-controls="settings-tabpanel-1" />
            <Tab label="Google Maps" id="settings-tab-2" aria-controls="settings-tabpanel-2" />
            <Tab label="Xero" id="settings-tab-3" aria-controls="settings-tabpanel-3" />
            <Tab label="Payroll" id="settings-tab-4" aria-controls="settings-tabpanel-4" />
            <Tab label="Setup" id="settings-tab-5" aria-controls="settings-tabpanel-5" />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Typography variant="h6" gutterBottom>
            School Default Prices
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Set default pricing for school orders based on payment model. These values will be automatically applied when creating new school orders.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ad Supported School Price Default"
                type="number"
                value={defaultPrices.adSupportedSchoolPrice}
                onChange={(e) => handlePriceChange('adSupportedSchoolPrice', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  step: 0.01
                }}
                helperText="Default price for Ad Supported school orders"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Paid School Price Default"
                type="number"
                value={defaultPrices.paidSchoolPrice}
                onChange={(e) => handlePriceChange('paidSchoolPrice', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  step: 0.01
                }}
                helperText="Default price for Paid school orders"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveSettings}
              disabled={loading || !hasChanges()}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>

            {hasChanges() && (
              <Button
                variant="outlined"
                onClick={resetChanges}
                disabled={loading}
              >
                Reset Changes
              </Button>
            )}
          </Box>

          {hasChanges() && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You have unsaved changes. Click "Save Settings" to apply them.
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Stationery Order Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure default percentages and fees for stationery orders. These values will be used to calculate margins, fees, and GST automatically.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Margin Percentage"
                type="number"
                value={stationerySettings.marginPercentage}
                onChange={(e) => handleStationeryChange('marginPercentage', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.1
                }}
                helperText="Percentage added to the base price as margin"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Artwork Fee"
                type="number"
                value={stationerySettings.artworkFee}
                onChange={(e) => handleStationeryChange('artworkFee', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  step: 0.01
                }}
                helperText="Base artwork fee (additional hours charged at same rate)"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Setup Fee"
                type="number"
                value={stationerySettings.setupFee}
                onChange={(e) => handleStationeryChange('setupFee', e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  step: 0.01
                }}
                helperText="Fixed setup fee for stationery orders"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="GST Percentage"
                type="number"
                value={stationerySettings.gstPercentage}
                onChange={(e) => handleStationeryChange('gstPercentage', e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{
                  min: 0,
                  max: 50,
                  step: 0.1
                }}
                helperText="GST percentage applied to total before discount"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveSettings}
              disabled={loading || !hasStationeryChanges()}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>

            {hasStationeryChanges() && (
              <Button
                variant="outlined"
                onClick={resetStationeryChanges}
                disabled={loading}
              >
                Reset Changes
              </Button>
            )}
          </Box>

          {hasStationeryChanges() && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You have unsaved changes. Click "Save Settings" to apply them.
            </Alert>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <GoogleMapsSettings />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            Xero Integration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Connect Xero to automatically push invoices, sync contacts, and send payments to employees via Xero Payroll.
          </Typography>

          {/* Environment badge */}
          {xeroStatus && (
            <Box sx={{ mb: 3 }}>
              <Chip
                label={xeroStatus.environment === 'demo' ? 'DEV / DEMO' : 'LIVE'}
                color={xeroStatus.environment === 'demo' ? 'success' : 'primary'}
                size="small"
                sx={{ mr: 1, fontWeight: 700 }}
              />
              {xeroStatus.connected && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Connected"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
          )}

          {xeroStatus?.connected ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Xero is connected. Invoices, contacts, and payroll are syncing automatically.
              </Alert>
              {xeroStatus.expiry && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Token expires: {new Date(xeroStatus.expiry).toLocaleString('en-AU')}
                </Typography>
              )}
              {isAdmin && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={xeroVerifying ? <CircularProgress size={16} /> : null}
                    onClick={handleXeroVerify}
                    disabled={xeroVerifying}
                  >
                    {xeroVerifying ? 'Testing...' : 'Verify Connection'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={xeroLoading ? <CircularProgress size={16} /> : <LinkOffIcon />}
                    onClick={handleXeroDisconnect}
                    disabled={xeroLoading}
                  >
                    Disconnect Xero
                  </Button>
                </Box>
              )}
              {xeroVerifyResult && (
                <Alert severity={xeroVerifyResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
                  <strong>{xeroVerifyResult.success ? 'Live API OK' : 'API Error'}</strong>
                  {' — '}{xeroVerifyResult.message}
                  {xeroVerifyResult.tokenExpired === true && (
                    <span> (<strong>Token is expired</strong> — reconnect to refresh)</span>
                  )}
                  {xeroVerifyResult.tokenExpired === false && (
                    <span> (Token is still valid)</span>
                  )}
                  {xeroVerifyResult.apiError && (
                    <Box component="pre" sx={{ mt: 1, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {xeroVerifyResult.apiError}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          ) : (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Xero is not connected. Connect to enable invoice syncing and payroll.
              </Alert>
              {isAdmin ? (
                <Button
                  variant="contained"
                  startIcon={xeroLoading ? <CircularProgress size={16} /> : <LinkIcon />}
                  onClick={handleXeroConnect}
                  disabled={xeroLoading}
                  sx={{ bgcolor: '#13B5EA', '&:hover': { bgcolor: '#0d8fc0' } }}
                >
                  {xeroLoading ? 'Redirecting...' : 'Connect to Xero'}
                </Button>
              ) : (
                <Alert severity="info">
                  Contact an admin to connect Xero.
                </Alert>
              )}
            </Box>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="body2" color="text.secondary">
            After connecting, Xero will be used for all invoicing. The environment shown above ({xeroStatus?.environment === 'demo' ? 'Demo Company' : 'Live'}) is determined by the server configuration.
          </Typography>

          <Divider sx={{ my: 3 }} />

          {/* Live Xero Activity Log */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6">Xero Activity Log</Typography>
            <Button size="small" startIcon={<RefreshIcon />} onClick={fetchXeroActivity} disabled={activityLoading}>
              Refresh
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Live feed of all Xero events — connections, token refreshes, contact syncs, invoice pushes, and errors. Cleared on server restart.
          </Typography>

          {xeroActivity.length === 0 ? (
            <Alert severity="info">No Xero activity yet. Events will appear here as they happen.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 24 }} />
                    <TableCell>Time</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Order</TableCell>
                    <TableCell>Message</TableCell>
                    <TableCell>Detail</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {xeroActivity.map((ev) => (
                    <TableRow key={ev.id} sx={{ bgcolor: ev.success ? 'inherit' : 'error.50' }}>
                      <TableCell>
                        {ev.success
                          ? <CheckCircleOutlineIcon fontSize="small" color="success" />
                          : <ErrorIcon fontSize="small" color="error" />}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                        {new Date(ev.ts).toLocaleTimeString('en-AU')}
                      </TableCell>
                      <TableCell>
                        <Chip label={ev.type.replace('_', ' ')} size="small"
                          color={ev.success ? 'default' : 'error'} variant="outlined" />
                      </TableCell>
                      <TableCell>{ev.orderId || '—'}</TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{ev.message}</TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.detail || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <Typography variant="h6" gutterBottom>
            Commission Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure the bonus paid to sales users when they reach their monthly invoiced target.
            The 20% overage commission rate is fixed and applied automatically to any invoiced amount over target.
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Target Bonus Amount ($)"
                type="number"
                value={payrollSettings.commission_target_bonus}
                onChange={e => setPayrollSettings(prev => ({ ...prev, commission_target_bonus: parseFloat(e.target.value) || 0 }))}
                inputProps={{ min: 0, step: 50 }}
                helperText="One-time bonus paid when a sales user reaches their monthly target (e.g. $500)"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={saveSettings}
              disabled={loading}
            >
              Save Settings
            </Button>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <Typography variant="h6" gutterBottom>
            Setup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Import organisations in bulk from a CSV or Excel file.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<CloudUploadIcon />}
            onClick={() => setBulkImportOpen(true)}
          >
            Bulk Import
          </Button>
        </TabPanel>
      </Paper>

      <BulkImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onImportComplete={() => {
          setSnackbar({
            open: true,
            message: 'Organisations imported successfully',
            severity: 'success'
          });
        }}
        token={token}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Settings;
